import { useCallback, type PropsWithChildren, type ReactNode } from "react";
import { ConsoleLabel } from "@/components/atoms/consoleLabel";
import { cn } from "@/lib/utils";
import { useEditorDrawerNavigation } from "@/features/sparxsEditor/contexts/EditorDrawerNavigationContext";
import { EditorDrawerCloseButton } from "@/features/sparxsEditor/components/atoms/EditorDrawerCloseButton";

export interface EditorSectionProperties extends PropsWithChildren {
  /** Header content (e.g. section title from EDITOR_FIELD_HEADERS). */
  header: ReactNode;
  /** When true, omit the outer div with data-editor="field-container". */
  noContainer?: boolean;
  /** Optional class for the header element. */
  headerClassName?: string;
}

const HEADER_ROW_CLASS = "mb-2 flex items-center gap-2";

const EditorSectionHeader = ({
  header,
  headerClassName,
}: Pick<EditorSectionProperties, "header" | "headerClassName">) => {
  const navigation = useEditorDrawerNavigation();

  const registerFieldHeader = navigation?.registerFieldHeader;

  const registerHeaderRowRef = useCallback(
    (element: HTMLDivElement | null) => {
      registerFieldHeader?.(element);
    },
    [registerFieldHeader],
  );

  return (
    <div ref={registerHeaderRowRef} className={HEADER_ROW_CLASS}>
      <ConsoleLabel
        data-editor="field-header"
        tone="link"
        className={cn("mb-0 shrink-0 p-[3px] text-left", headerClassName)}
      >
        {header}
      </ConsoleLabel>
      {navigation != null ? <EditorDrawerCloseButton variant="inline" /> : null}
      <div className="min-w-0 flex-1" aria-hidden="true" />
    </div>
  );
};

export const EditorSection = ({
  header,
  noContainer = false,
  headerClassName,
  children,
}: EditorSectionProperties) => {
  if (children == null) {
    return null;
  }
  return noContainer ? (
    <>
      <EditorSectionHeader header={header} headerClassName={headerClassName} />
      <div>{children}</div>
    </>
  ) : (
    <div data-editor="field-container" className="text-left">
      <EditorSectionHeader header={header} headerClassName={headerClassName} />
      <div>{children}</div>
    </div>
  );
};
